<template>
  <div class="site-shell">
    <header class="site-header">
      <a class="brand" href="#top" aria-label="PickleBear Systems home">
        <img :src="brandMark" alt="" width="458" height="467" decoding="async" fetchpriority="high" />
        <span>PickleBear <strong>Systems</strong></span>
      </a>
      <nav aria-label="Primary navigation">
        <a href="#services">Services</a>
        <a href="#approach">Approach</a>
        <a class="nav-cta" href="mailto:bogliconsulting@gmail.com">Start a project</a>
      </nav>
    </header>

    <main id="top">
      <section class="hero" aria-labelledby="hero-title">
        <div class="hero-copy">
          <p class="eyebrow"><span></span> Independent software studio</p>
          <h1 id="hero-title">Complex systems.<br /><em>Clear outcomes.</em></h1>
          <p class="hero-lede">
            We design and build focused software, data platforms, and cloud systems
            for teams ready to move beyond workarounds.
          </p>
          <div class="hero-actions">
            <a class="button button-primary" href="mailto:bogliconsulting@gmail.com">
              Tell us what you’re building <span aria-hidden="true">↗</span>
            </a>
            <a class="text-link" href="#services">Explore our capabilities <span aria-hidden="true">↓</span></a>
          </div>
          <ul class="trust-list" aria-label="Core capabilities">
            <li>Product engineering</li>
            <li>Data &amp; automation</li>
            <li>Cloud architecture</li>
          </ul>
        </div>

        <div class="hero-visual" aria-label="PickleBear Systems delivery model">
          <div class="visual-topline">
            <span>PB / SYSTEM_01</span>
            <span class="status"><i></i> Ready to build</span>
          </div>
          <div class="visual-core">
            <div class="orbit orbit-one"></div>
            <div class="orbit orbit-two"></div>
            <div class="pickle-friend-frame">
              <img
                :src="pickleFriend"
                alt="PickleBear hugging a pickle"
                width="800"
                height="800"
                decoding="async"
                fetchpriority="high"
              />
            </div>
            <span class="node node-a">DISCOVER</span>
            <span class="node node-b">BUILD</span>
            <span class="node node-c">SHIP</span>
          </div>
          <div class="visual-footer">
            <p>Practical technology,<br />built around the work.</p>
            <span>01—03</span>
          </div>
        </div>
      </section>

      <section id="services" class="section services-section" aria-labelledby="services-title">
        <div class="section-intro">
          <div>
            <p class="section-label">01 / What we do</p>
            <h2 id="services-title">Build the thing your business <em>actually</em> needs.</h2>
          </div>
          <p>
            From the first useful version to the systems behind it, we turn operational
            complexity into software people can rely on.
          </p>
        </div>

        <div class="service-grid">
          <article class="service-card">
            <div class="card-number">01</div>
            <div class="service-symbol" aria-hidden="true"><span></span><span></span><span></span></div>
            <h3>Custom software</h3>
            <p>Focused products, internal tools, client portals, and reliable APIs—designed around the way your team works.</p>
            <ul>
              <li>Product strategy</li>
              <li>Front-end systems</li>
              <li>Back-end &amp; APIs</li>
            </ul>
          </article>
          <article class="service-card service-card-featured">
            <div class="card-number">02</div>
            <div class="service-symbol symbol-data" aria-hidden="true"><span></span><span></span><span></span></div>
            <h3>Data &amp; automation</h3>
            <p>Clean pipelines, useful reporting, and automated workflows that help decisions happen with less friction.</p>
            <ul>
              <li>Data engineering</li>
              <li>Analytics</li>
              <li>AI-enabled workflows</li>
            </ul>
          </article>
          <article class="service-card">
            <div class="card-number">03</div>
            <div class="service-symbol symbol-cloud" aria-hidden="true"><span></span><span></span><span></span></div>
            <h3>Cloud &amp; platform</h3>
            <p>Modern infrastructure built for sensible cost, smooth delivery, and confidence when the stakes get higher.</p>
            <ul>
              <li>Cloud architecture</li>
              <li>Infrastructure as code</li>
              <li>Reliability</li>
            </ul>
          </article>
        </div>
      </section>

      <section class="statement" aria-label="PickleBear Systems point of view">
        <p>The goal isn’t more software.</p>
        <p>It’s <em>less friction.</em></p>
        <span>PB / PRINCIPLE_01</span>
      </section>

      <section id="approach" class="section approach-section" aria-labelledby="approach-title">
        <div class="approach-heading">
          <p class="section-label light">02 / How we work</p>
          <h2 id="approach-title">From first sketch<br />to stable release.</h2>
          <p>One clear process, visible decisions, and senior attention all the way through.</p>
        </div>
        <ol class="process-list">
          <li>
            <span class="process-number">01</span>
            <div><h3>Understand</h3><p>We map the work, the users, and the constraint that matters most.</p></div>
          </li>
          <li>
            <span class="process-number">02</span>
            <div><h3>Design</h3><p>We shape the smallest coherent solution and make the tradeoffs visible.</p></div>
          </li>
          <li>
            <span class="process-number">03</span>
            <div><h3>Build</h3><p>We ship in useful slices, test the important paths, and keep momentum tangible.</p></div>
          </li>
          <li>
            <span class="process-number">04</span>
            <div><h3>Evolve</h3><p>We leave the system clearer, documented, and ready for what comes next.</p></div>
          </li>
        </ol>
      </section>

      <section class="section studio-section" aria-labelledby="studio-title">
        <div class="studio-mark">
          <div class="mark-frame">
            <span>EST.</span>
            <img
              :src="brandMark"
              alt="PickleBear mascot"
              width="458"
              height="467"
              loading="lazy"
              decoding="async"
            />
            <span>SYSTEMS</span>
          </div>
        </div>
        <div class="studio-copy">
          <p class="section-label">03 / Why PickleBear</p>
          <h2 id="studio-title">Small by design.<br /><em>Serious</em> about the work.</h2>
          <p class="studio-lede">
            We work where product thinking and engineering execution need to happen
            in the same room. Fewer layers mean faster learning and better decisions.
          </p>
          <div class="principles">
            <div><span>01</span><p><strong>Direct collaboration.</strong> You work with the people doing the thinking and the building.</p></div>
            <div><span>02</span><p><strong>Practical choices.</strong> Technology follows the problem, not the trend cycle.</p></div>
            <div><span>03</span><p><strong>Built to be owned.</strong> Clear systems and shared knowledge stay with your team.</p></div>
          </div>
        </div>
      </section>

      <section id="contact" class="contact-section" aria-labelledby="contact-title">
        <div>
          <p class="section-label">04 / Start a conversation</p>
          <h2 id="contact-title">Have a complicated problem?<br /><em>Good.</em></h2>
        </div>
        <div class="contact-action">
          <p>Tell us what’s stuck, what you’ve tried, or what you want to make possible.</p>
          <a href="mailto:bogliconsulting@gmail.com">bogliconsulting@gmail.com <span aria-hidden="true">↗</span></a>
          <a class="phone-link" href="tel:+19412662365">+1 941 266 2365</a>
        </div>
      </section>
    </main>

    <footer>
      <a class="brand footer-brand" href="#top" aria-label="Back to top">
        <img :src="brandMark" alt="" width="458" height="467" loading="lazy" decoding="async" />
        <span>PickleBear <strong>Systems</strong></span>
      </a>
      <p>Software engineering · Data systems · Cloud infrastructure</p>
      <p>© {{ currentYear }} PickleBear Systems</p>
    </footer>
  </div>
</template>

<script>
import brandMark from '@/assets/logo-clean.webp'
import pickleFriend from '@/assets/picklebear-transparent.webp'

export default {
  name: 'App',
  data() {
    return {
      brandMark,
      pickleFriend,
      currentYear: new Date().getFullYear()
    }
  }
}
</script>

<style>
:root {
  --ink: #08130f;
  --paper: #f2f1e9;
  --lime: #b6f36c;
  --green: #4c8f43;
  --line: rgba(8, 19, 15, 0.18);
  --muted: #58645f;
}

* { box-sizing: border-box; }

html { scroll-behavior: smooth; }

body {
  margin: 0;
  background: var(--paper);
  color: var(--ink);
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
}

body::before {
  position: fixed;
  inset: 0;
  z-index: 10;
  pointer-events: none;
  content: "";
  opacity: 0.035;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 180 180' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='.8' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='.45'/%3E%3C/svg%3E");
}

a { color: inherit; }

.site-shell { min-height: 100vh; overflow: hidden; }

.site-header {
  width: min(1400px, calc(100% - 64px));
  height: 96px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--line);
}

.brand {
  display: inline-flex;
  align-items: center;
  gap: 11px;
  color: var(--ink);
  text-decoration: none;
  font-size: 17px;
  font-weight: 650;
  letter-spacing: -0.02em;
}

.brand img { width: 42px; height: 42px; object-fit: contain; }
.brand strong { color: var(--green); font-weight: 650; }

nav { display: flex; align-items: center; gap: 34px; }
nav a { text-decoration: none; font-size: 14px; font-weight: 600; }
.nav-cta { padding: 12px 18px; border: 1px solid var(--ink); border-radius: 999px; }

.hero {
  width: min(1400px, calc(100% - 64px));
  min-height: calc(100vh - 96px);
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1.08fr 0.92fr;
  align-items: center;
  gap: clamp(48px, 7vw, 112px);
  padding: 76px 0 92px;
}

.eyebrow {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 34px;
  color: var(--muted);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.15em;
  text-transform: uppercase;
}

.eyebrow span { width: 28px; height: 2px; background: var(--green); }

h1 {
  max-width: 820px;
  margin: 0;
  font-size: clamp(60px, 7.1vw, 112px);
  font-weight: 580;
  line-height: 0.9;
  letter-spacing: -0.075em;
}

h1 em { color: var(--green); font-family: Georgia, "Times New Roman", serif; font-weight: 400; }

.hero-lede {
  max-width: 640px;
  margin: 38px 0 0;
  color: var(--muted);
  font-size: clamp(18px, 1.5vw, 23px);
  line-height: 1.48;
  letter-spacing: -0.02em;
}

.hero-actions { display: flex; align-items: center; gap: 28px; margin-top: 38px; }

.button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  min-height: 58px;
  padding: 0 24px;
  border-radius: 4px;
  text-decoration: none;
  font-size: 15px;
  font-weight: 700;
}

.button-primary { background: var(--ink); color: #fff; }
.button-primary span { color: var(--lime); font-size: 20px; }
.text-link { font-size: 14px; font-weight: 700; text-underline-offset: 5px; }

.button,
.nav-cta,
.text-link,
.contact-action a { transition: transform .2s ease, background-color .2s ease, color .2s ease, border-color .2s ease; }
.button:hover { transform: translateY(-2px); }
.nav-cta:hover { background: var(--ink); color: #fff; }
.text-link:hover { color: var(--green); }

a:focus-visible { outline: 3px solid var(--lime); outline-offset: 4px; }

.trust-list {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 62px 0 0;
  padding: 19px 0 0;
  border-top: 1px solid var(--line);
  list-style: none;
}

.trust-list li { color: var(--muted); font-size: 12px; font-weight: 700; letter-spacing: 0.08em; text-transform: uppercase; }

.hero-visual {
  position: relative;
  min-height: 640px;
  padding: 24px;
  overflow: hidden;
  background: var(--ink);
  color: #f7f8f3;
  border-radius: 3px;
  box-shadow: 0 40px 80px rgba(8, 19, 15, 0.18);
}

.hero-visual::before,
.hero-visual::after {
  position: absolute;
  content: "";
  border-radius: 50%;
  filter: blur(1px);
}

.hero-visual::before { width: 360px; height: 360px; top: 95px; right: -70px; background: rgba(182, 243, 108, 0.24); }
.hero-visual::after { width: 260px; height: 260px; bottom: -120px; left: -80px; background: rgba(76, 143, 67, 0.28); }

.visual-topline,
.visual-footer {
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  font-family: "Courier New", monospace;
  font-size: 11px;
  letter-spacing: 0.1em;
}

.status { display: flex; align-items: center; gap: 8px; }
.status i { width: 7px; height: 7px; border-radius: 50%; background: var(--lime); box-shadow: 0 0 16px var(--lime); }

.visual-core { position: relative; height: 500px; display: grid; place-items: center; }
.pickle-friend-frame {
  position: relative;
  z-index: 3;
  width: min(72%, 360px);
  aspect-ratio: 1;
  overflow: hidden;
  background: #d9e3e8;
  border: 6px solid rgba(247, 248, 243, 0.94);
  border-radius: 30px;
  box-shadow: 0 28px 46px rgba(0, 0, 0, 0.38);
  transform: rotate(-2.5deg);
}
.pickle-friend-frame::after {
  position: absolute;
  inset: 0;
  content: "";
  box-shadow: inset 0 0 0 1px rgba(8, 19, 15, 0.12);
  border-radius: 23px;
  pointer-events: none;
}
.pickle-friend-frame img { display: block; width: 100%; height: 100%; object-fit: cover; }
.orbit { position: absolute; border: 1px solid rgba(255,255,255,.18); border-radius: 50%; }
.orbit-one { width: 390px; height: 390px; }
.orbit-two { width: 520px; height: 520px; border-style: dashed; animation: spin 36s linear infinite; }
.node { position: absolute; z-index: 4; padding: 7px 10px; background: var(--lime); color: var(--ink); font: 700 10px/1 "Courier New", monospace; letter-spacing: .1em; }
.node-a { top: 22%; left: 4%; }
.node-b { top: 46%; right: 0; }
.node-c { bottom: 16%; left: 10%; }
.visual-footer { align-items: end; }
.visual-footer p { margin: 0; font-family: "Helvetica Neue", sans-serif; font-size: 19px; font-weight: 550; line-height: 1.25; letter-spacing: -0.02em; }

.section { width: min(1400px, calc(100% - 64px)); margin: 0 auto; }
.services-section { padding: 148px 0 160px; }

.section-label {
  margin: 0 0 28px;
  color: var(--green);
  font: 700 11px/1 "Courier New", monospace;
  letter-spacing: .14em;
  text-transform: uppercase;
}

.section-label.light { color: var(--lime); }

.section-intro {
  display: grid;
  grid-template-columns: 1.45fr .55fr;
  align-items: end;
  gap: 80px;
  margin-bottom: 78px;
}

h2 {
  margin: 0;
  font-size: clamp(48px, 5.6vw, 88px);
  font-weight: 580;
  line-height: .98;
  letter-spacing: -.065em;
}

h2 em { color: var(--green); font-family: Georgia, "Times New Roman", serif; font-weight: 400; }
.section-intro > p { max-width: 460px; margin: 0; color: var(--muted); font-size: 18px; line-height: 1.55; }

.service-grid { display: grid; grid-template-columns: repeat(3, 1fr); border-top: 1px solid var(--line); border-left: 1px solid var(--line); }
.service-card { min-height: 560px; padding: 28px 30px 34px; border-right: 1px solid var(--line); border-bottom: 1px solid var(--line); display: flex; flex-direction: column; }
.service-card-featured { background: #e6f3cf; }
.card-number { color: var(--muted); font: 700 11px/1 "Courier New", monospace; }

.service-symbol { position: relative; width: 134px; height: 134px; margin: 58px 0 70px; border: 1px solid var(--ink); border-radius: 50%; }
.service-symbol::before,
.service-symbol::after,
.service-symbol span { position: absolute; content: ""; }
.service-symbol::before { inset: 25px; border: 1px solid var(--ink); border-radius: 50%; }
.service-symbol::after { width: 24px; height: 24px; top: -5px; left: 54px; background: var(--green); border-radius: 50%; }
.service-symbol span { width: 9px; height: 9px; background: var(--ink); border-radius: 50%; }
.service-symbol span:nth-child(1) { top: 33px; right: 18px; }
.service-symbol span:nth-child(2) { bottom: 18px; left: 32px; }
.service-symbol span:nth-child(3) { top: 63px; left: -5px; }
.symbol-data { border-radius: 2px; transform: rotate(45deg); }
.symbol-data::before { border-radius: 2px; }
.symbol-data::after { top: 50px; left: 50px; }
.symbol-data span { transform: rotate(-45deg); }
.symbol-cloud { border-style: dashed; }
.symbol-cloud::before { border-style: dashed; }

.service-card h3 { margin: 0 0 18px; font-size: 30px; font-weight: 620; letter-spacing: -.04em; }
.service-card > p { margin: 0; color: var(--muted); font-size: 16px; line-height: 1.55; }
.service-card ul { display: flex; flex-wrap: wrap; gap: 8px; margin: auto 0 0; padding: 0; list-style: none; }
.service-card li { padding: 8px 10px; border: 1px solid var(--line); color: var(--muted); font: 700 10px/1 "Courier New", monospace; text-transform: uppercase; }

.statement {
  position: relative;
  min-height: 72vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 110px max(32px, calc((100vw - 1400px) / 2));
  overflow: hidden;
  background: var(--lime);
  color: var(--ink);
}
.statement::after { position: absolute; width: 46vw; height: 46vw; right: -12vw; top: -12vw; border: 1px solid rgba(8,19,15,.22); border-radius: 50%; box-shadow: 0 0 0 7vw rgba(8,19,15,.04), 0 0 0 14vw rgba(8,19,15,.04); content: ""; }
.statement p { position: relative; z-index: 1; margin: 0; font-size: clamp(55px, 7vw, 112px); font-weight: 580; line-height: .96; letter-spacing: -.07em; }
.statement p:nth-child(2) { margin-left: 13vw; }
.statement em { font-family: Georgia, "Times New Roman", serif; font-weight: 400; }
.statement span { position: absolute; bottom: 32px; left: max(32px, calc((100vw - 1400px) / 2)); font: 700 11px/1 "Courier New", monospace; letter-spacing: .14em; }

.approach-section { display: grid; grid-template-columns: .8fr 1.2fr; gap: 11vw; padding: 158px 0; }
.approach-section::before { position: absolute; left: 0; right: 0; z-index: -1; height: 100%; margin-top: -158px; background: var(--ink); content: ""; }
.approach-section { position: relative; color: #f7f8f3; }
.approach-heading { position: sticky; top: 50px; align-self: start; }
.approach-heading > p:last-child { max-width: 430px; margin: 32px 0 0; color: #aab5af; font-size: 18px; line-height: 1.55; }
.process-list { margin: 0; padding: 0; border-top: 1px solid rgba(255,255,255,.18); list-style: none; }
.process-list li { display: grid; grid-template-columns: 74px 1fr; gap: 22px; padding: 34px 0 38px; border-bottom: 1px solid rgba(255,255,255,.18); }
.process-number { padding-top: 7px; color: var(--lime); font: 700 11px/1 "Courier New", monospace; }
.process-list h3 { margin: 0 0 12px; font-size: 32px; font-weight: 580; letter-spacing: -.04em; }
.process-list p { max-width: 530px; margin: 0; color: #aab5af; font-size: 16px; line-height: 1.6; }

.studio-section { display: grid; grid-template-columns: .85fr 1.15fr; gap: 10vw; align-items: center; padding: 160px 0; }
.studio-mark { display: grid; place-items: center; min-height: 630px; overflow: hidden; background: #dceac6; }
.mark-frame { width: min(77%, 430px); aspect-ratio: 1; display: grid; grid-template-rows: auto 1fr auto; padding: 24px; border: 1px solid var(--ink); border-radius: 50%; text-align: center; }
.mark-frame img { place-self: center; width: 68%; height: auto; object-fit: contain; filter: drop-shadow(0 20px 20px rgba(8,19,15,.18)); }
.mark-frame span { font: 700 10px/1 "Courier New", monospace; letter-spacing: .18em; }
.studio-copy h2 { font-size: clamp(48px, 5vw, 78px); }
.studio-lede { max-width: 620px; margin: 36px 0 54px; color: var(--muted); font-size: 20px; line-height: 1.55; }
.principles { border-top: 1px solid var(--line); }
.principles div { display: grid; grid-template-columns: 54px 1fr; gap: 18px; padding: 22px 0; border-bottom: 1px solid var(--line); }
.principles span { color: var(--green); font: 700 10px/1.6 "Courier New", monospace; }
.principles p { margin: 0; color: var(--muted); font-size: 15px; line-height: 1.55; }
.principles strong { color: var(--ink); }

.contact-section { width: min(1400px, calc(100% - 64px)); margin: 0 auto 32px; display: grid; grid-template-columns: 1.1fr .9fr; gap: 9vw; padding: 86px; background: var(--green); color: #fff; }
.contact-section .section-label { color: var(--lime); }
.contact-section h2 { font-size: clamp(50px, 5.2vw, 82px); }
.contact-section h2 em { color: var(--lime); }
.contact-action { align-self: end; }
.contact-action > p { max-width: 480px; margin: 0 0 38px; color: rgba(255,255,255,.78); font-size: 18px; line-height: 1.55; }
.contact-action a { display: flex; justify-content: space-between; padding: 22px 0; border-top: 1px solid rgba(255,255,255,.36); color: #fff; text-decoration: none; font-size: clamp(16px, 1.5vw, 23px); font-weight: 650; letter-spacing: -.02em; }
.contact-action a:hover { color: var(--lime); }
.contact-action .phone-link { font-size: 15px; letter-spacing: .02em; }

footer { width: min(1400px, calc(100% - 64px)); min-height: 130px; margin: 0 auto; display: grid; grid-template-columns: 1fr auto auto; align-items: center; gap: 60px; }
.footer-brand img { width: 36px; height: 36px; }
footer p { margin: 0; color: var(--muted); font-size: 11px; font-weight: 650; letter-spacing: .06em; text-transform: uppercase; }

@keyframes spin { to { transform: rotate(360deg); } }

@media (max-width: 980px) {
  .site-header, .hero, .section, .contact-section, footer { width: min(100% - 40px, 760px); }
  nav > a:not(.nav-cta) { display: none; }
  .hero { grid-template-columns: 1fr; padding-top: 72px; }
  .hero-visual { min-height: 560px; }
  .section-intro, .approach-section, .studio-section, .contact-section { grid-template-columns: 1fr; }
  .section-intro { gap: 30px; }
  .service-grid { grid-template-columns: 1fr; }
  .service-card { min-height: 480px; }
  .approach-section { gap: 72px; }
  .approach-heading { position: static; }
  .studio-mark { min-height: 520px; }
  .contact-section { gap: 70px; padding: 64px; }
  footer { grid-template-columns: 1fr 1fr; padding: 34px 0; }
  footer p:nth-child(2) { display: none; }
  footer p:last-child { text-align: right; }
}

@media (max-width: 620px) {
  .site-header, .hero, .section, .contact-section, footer { width: calc(100% - 32px); }
  .site-header { height: 78px; }
  .brand span { display: none; }
  .brand img { width: 38px; height: 38px; }
  .nav-cta { padding: 10px 14px; }
  .hero { min-height: auto; padding: 58px 0 72px; }
  h1 { font-size: clamp(52px, 17vw, 78px); }
  .hero-actions { align-items: stretch; flex-direction: column; }
  .button { width: 100%; }
  .text-link { align-self: center; }
  .trust-list { gap: 14px 22px; margin-top: 46px; }
  .hero-visual { min-height: 470px; padding: 18px; }
  .visual-core { height: 355px; }
  .pickle-friend-frame { width: min(74%, 270px); border-width: 4px; border-radius: 22px; }
  .orbit-one { width: 260px; height: 260px; }
  .orbit-two { width: 360px; height: 360px; }
  .visual-footer p { font-size: 16px; }
  .services-section, .approach-section, .studio-section { padding: 100px 0; }
  .section-intro { margin-bottom: 52px; }
  .service-card { min-height: 460px; padding: 24px; }
  .service-symbol { margin: 48px 0 60px; }
  .statement { min-height: 60vh; padding-left: 16px; padding-right: 16px; }
  .statement p:nth-child(2) { margin-left: 0; }
  .statement span { left: 16px; }
  .approach-section::before { margin-top: -100px; }
  .process-list li { grid-template-columns: 42px 1fr; }
  .studio-mark { min-height: 380px; }
  .contact-section { margin-bottom: 0; padding: 44px 24px; }
  footer { grid-template-columns: 1fr; gap: 22px; }
  footer p:last-child { text-align: left; }
}

@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  .orbit-two { animation: none; }
}
</style>
